/**
 * Which materials are YouTube films the card can play in place, and the one address it plays them from.
 *
 * ── The player's address is BUILT, never copied ─────────────────────────────────────────────
 * A material's url is trainer input. The iframe never sees it: this module reads an eleven-character
 * video id out of it and composes `youtube-nocookie.com/embed/<id>` from constants, so whatever else
 * the trainer's address carried — a playlist, a tracking parameter, a scheme trick the link gate has
 * not met yet — cannot reach the frame. An id that is not exactly eleven characters of YouTube's own
 * alphabet is not a video, and the link stays a plain link.
 *
 * ── The host is matched EXACTLY ─────────────────────────────────────────────────────────────
 * `youtube.com`, its `www.` and `m.` spellings, `youtube-nocookie.com` and `youtu.be`. Not a suffix
 * match: `youtube.com.example.net` and `notyoutube.com` are somebody else's pages, and `music.` or
 * `studio.` subdomains do not carry films a client should watch.
 *
 * ── A Short is drawn upright ────────────────────────────────────────────────────────────────
 * `/shorts/<id>` is the only spelling that says the film is vertical, so it is the one this module
 * reports as `vertical`. The frame then stands 9:16 in the middle of the box and is never cropped:
 * a movement cut off at the knees is not a demonstration.
 *
 * Pure: no DOM, no React.
 */

export interface YouTubeVideo {
  readonly id: string;
  /** Where the trainer's link asked the film to start, in whole seconds. 0 when it did not. */
  readonly startSeconds: number;
  /** A `/shorts/` link — the frame stands upright rather than filling the box. */
  readonly vertical: boolean;
}

const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const WATCH_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);
const SHORT_HOSTS = new Set(['youtu.be', 'www.youtu.be']);
/** Path forms on the watch hosts that carry the id as their second segment. */
const ID_PATHS = new Set(['shorts', 'embed', 'live', 'v']);

/** `90`, `90s`, `1m30s`, `1h2m3s` — the spellings YouTube writes into `t=` and `start=`. */
function secondsOf(value: string | null): number {
  if (value === null) return 0;
  if (/^\d+$/.test(value)) return Number(value);
  const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(value);
  if (match === null || value === '') return 0;
  const [, h = '0', m = '0', s = '0'] = match;
  return Number(h) * 3600 + Number(m) * 60 + Number(s);
}

/** The film a material points at, or `null` when it is not a YouTube film this card can play. */
export function youtubeVideoOf(url: string): YouTubeVideo | null {
  let address: URL;
  try {
    address = new URL(url);
  } catch {
    return null;
  }
  if (address.protocol !== 'https:' && address.protocol !== 'http:') return null;

  const host = address.hostname.toLowerCase().replace(/\.$/, '');
  const segments = address.pathname.split('/').filter((segment) => segment !== '');
  let id: string | undefined;
  let vertical = false;

  if (SHORT_HOSTS.has(host)) {
    id = segments[0];
  } else if (WATCH_HOSTS.has(host)) {
    if (segments[0] === 'watch') {
      id = address.searchParams.get('v') ?? undefined;
    } else if (segments[0] !== undefined && ID_PATHS.has(segments[0])) {
      id = segments[1];
      vertical = segments[0] === 'shorts';
    }
  }

  if (id === undefined || !VIDEO_ID.test(id)) return null;
  const startSeconds = secondsOf(address.searchParams.get('t') ?? address.searchParams.get('start'));
  return { id, startSeconds, vertical };
}

/**
 * The frame's address. `enablejsapi` with the page's own `origin` is what lets the frame REPORT an
 * owner's embedding refusal to the card (see `youtubeRefusal`), without loading YouTube's script
 * into this page.
 */
export function youtubeEmbedUrl(video: YouTubeVideo, origin: string): string {
  const query = new URLSearchParams({
    autoplay: '1',
    playsinline: '1',
    rel: '0',
    enablejsapi: '1',
    origin,
  });
  if (video.startSeconds > 0) query.set('start', String(video.startSeconds));
  return `https://www.youtube-nocookie.com/embed/${video.id}?${query.toString()}`;
}

/** The film's own still, from YouTube's image host — the tab's thumbnail and the refusal's backdrop. */
export function youtubeThumbnailUrl(video: YouTubeVideo): string {
  return `https://i.ytimg.com/vi/${video.id}/mqdefault.jpg`;
}

/** The frame's origin, which is the only origin whose messages the card reads. */
export const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube-nocookie.com';

/**
 * Whether a message the frame posted says the film cannot play here.
 *
 * The frame speaks JSON strings. `onError` with 101 or 150 is the owner refusing embedding; 100 is a
 * film that was removed or made private — to the trainer both read the same way, "watch it on
 * YouTube", because the link is the only way left. 2 (a bad id) and 5 (a player fault) are not
 * refusals and leave the frame to say what it says.
 */
export function youtubeRefusal(data: unknown): boolean {
  if (typeof data !== 'string') return false;
  let message: unknown;
  try {
    message = JSON.parse(data);
  } catch {
    return false;
  }
  if (typeof message !== 'object' || message === null) return false;
  const { event, info } = message as { event?: unknown; info?: unknown };
  return event === 'onError' && (info === 100 || info === 101 || info === 150);
}
